/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { createItemFulfillmentAvailabilityString } from '@/utils/itemHelpers/fulfillmentHelpers/createItemFulfillmentAvailabilityString';
import { type ProductSkuDetails } from '@/features/product';
import type { SkuDealsFromAPI } from '@/types/availability';
import { type ProductModel } from '@/api/types/products-types';
import { determineOptionsToDisplayButtons } from '@/features/fulfillment/api/optionsToDisplay';
import { type Locale } from '@/types/i18n';
import root from 'window-or-global';

const productDetailAnalytics = ({
  productDetails,
  skuDetailsData,
  autoCorrectedKeyword,
  searchRedirectText,
  hasDealBadge,
  isNewDealsApiEnabled,
  skuDealsData,
  vehicleFit,
  locale,
  vehicleFitmentLabel,
}: {
  productDetails: ProductModel;
  skuDetailsData: ProductSkuDetails | null;
  autoCorrectedKeyword: string;
  searchRedirectText: string;
  hasDealBadge: boolean | undefined;
  isNewDealsApiEnabled: boolean;
  skuDealsData: SkuDealsFromAPI[] | undefined;
  vehicleFit?: string;
  locale?: Locale;
  vehicleFitmentLabel: string | undefined;
}): {
  autoCorrectedKeyword: string;
  nddaftercutoff: Array<string>;
  productPartTypeIdList: Array<string>;
  sthSpecialOrderList: any[];
  dealBundleCountList: Array<string>;
  bopusList: boolean[];
  dealFlag: boolean[];
  fulfillmentAvailabilityList: Array<string>;
  nddEligibleList: boolean[];
  productBrand: string[];
  productCorePrice: string[];
  productId: string[];
  productPartNumberList: Array<string>;
  productLineCode: string[];
  productNotAvailable: boolean[];
  productPart: string[];
  productPrice: string[];
  productRepositoryId?: string[];
  partTerminologyId?: string[];
  partGroupId?: string[];
  sthList: boolean[];
  sthSpecialOrder: boolean[];
  vdp: boolean[];
  searchResultCount?: string;
  clearanceList: Array<string>;
  sisterStoreAvailableList: Array<string>;
  productFitmentCheckMessage: Array<string>;
  productDealMessage: string[];
  productFindingMethod: string;
  productFulfillmentOptionSeenByCustomer: string[];
} => {
  const { brandName, partTypeName, itemId, lineCode, partNumber, subBrandName, ecommProductId } =
    productDetails ?? {};
  let productNotAvailable = false;
  let productRepositoryIdFromShelfData = '';
  const fulfillmentAvailabilityList: string[] = [];
  const nddaftercutoff: string[] = [];
  const productPartTypeIdList: string[] = [];
  const sthSpecialOrderList: any[] = [];
  const clearanceList: string[] = [];
  const sisterStoreAvailableList: string[] = [];
  const productFitmentCheckMessage: string[] = [];
  const productDealMessage: string[] = [];
  const fulfillmentOptions =
    skuDetailsData?.skuPricingAndAvailability?.skuAvailabilityInfo.fulfillmentOptions;

  const sisterStoreAvailable = fulfillmentOptions?.filter((option) => {
    return option.sisterStoreAvailable === true;
  });

  if (fulfillmentOptions) {
    // @ts-expect-error FIXME
    fulfillmentAvailabilityList.push(createItemFulfillmentAvailabilityString(fulfillmentOptions));
  }

  if (sisterStoreAvailable?.length) {
    sisterStoreAvailableList.push('1');
  } else {
    sisterStoreAvailableList.push('');
  }

  if (!vehicleFit && vehicleFit !== 'MAY_NOT_FIT') {
    productFitmentCheckMessage.push('');
  }
  if (vehicleFit == 'NEEDS_MORE_INFO') {
    productFitmentCheckMessage.push('Need More Info');
  }
  if (vehicleFit === 'DOES_NOT_FIT') {
    productFitmentCheckMessage.push("doesn't fit your vehicle");
  }
  if (vehicleFit === 'FITS') {
    productFitmentCheckMessage.push(
      vehicleFitmentLabel === 'UNIVERSAL' || productDetails.recordType === 'non-app'
        ? ''
        : 'fits your vehicle',
    );
  }
  if (vehicleFit === 'NEEDS_BASE_VEHICLE') {
    productFitmentCheckMessage.push('check if this fits your vehicle');
  }
  if (vehicleFit === 'MAY_NOT_FIT') {
    productFitmentCheckMessage.push('may not fit your vehicle');
  }

  const dealsAvailableQty = isNewDealsApiEnabled
    ? skuDealsData?.[0]?.skuDealsInfo?.dealsAvailableQty
    : skuDetailsData?.skuPricingAndAvailability?.dealsAvailableQty;
  const dealConfigurableLabel = isNewDealsApiEnabled
    ? skuDealsData?.[0]?.skuDealsInfo?.dealConfigurableLabel
    : skuDetailsData?.skuPricingAndAvailability?.dealConfigurableLabel;
  const partHasDeals = (dealsAvailableQty ?? 0) > 0;
  const dealAvailableFlag = isNewDealsApiEnabled
    ? skuDealsData?.[0]?.skuDealsInfo?.dealAvailable
    : skuDetailsData?.skuPricingAndAvailability?.dealAvailable;
  const hasDiscountedPrice = !!skuDetailsData?.skuPricingAndAvailability?.discountedPrice;
  const isMXLocale = locale === 'es-MX';
  const wasRetailPrice = parseFloat(
    String(skuDetailsData?.skuPricingAndAvailability?.wasRetailPrice),
  );
  const retailPrice = parseFloat(String(skuDetailsData?.skuPricingAndAvailability?.retailPrice));
  const hasValidReduction =
    Number.isFinite(wasRetailPrice) &&
    Number.isFinite(retailPrice) &&
    wasRetailPrice > 0 &&
    retailPrice > 0 &&
    retailPrice < wasRetailPrice;
  const discountAmount = hasValidReduction ? wasRetailPrice - retailPrice : 0;
  const savingsPercent = hasValidReduction
    ? Math.ceil(100 - (retailPrice / wasRetailPrice) * 100)
    : 0;

  const dealsAvailable =
    partHasDeals &&
    !!dealAvailableFlag &&
    !!hasDealBadge &&
    (isMXLocale ? !hasDiscountedPrice : true);

  const dealBundleCountList =
    dealsAvailable && dealsAvailableQty ? [dealsAvailableQty.toString()] : [''];

  if (dealsAvailable) {
    const discountDisplay =
      discountAmount > savingsPercent ? `$${discountAmount}` : `${savingsPercent}%`;
    if (wasRetailPrice && discountAmount) {
      productDealMessage.push(
        `${dealConfigurableLabel || 'See Available Deals'} - Save ${discountDisplay}`,
      );
    } else {
      productDealMessage.push(`${dealConfigurableLabel || 'See Available Deals'}`);
    }
  } else {
    productDealMessage.push('');
  }
  const bopusList = [skuDetailsData?.skuPricingAndAvailability?.storePickupAvailable ?? false];
  const nddEligibleList = [skuDetailsData?.aZBadgesFlagVO?.eligibleForNextDay ?? false];
  const sthList = [skuDetailsData?.skuPricingAndAvailability?.shipToHomeAvailable ?? false];
  const dealFlag = dealAvailableFlag ?? false;
  const productPrice = (
    skuDetailsData?.skuPricingAndAvailability?.retailPrice.toString() ?? ''
  ).toString();
  skuDetailsData?.skuPricingAndAvailability?.skuAvailabilityInfo?.clearanceItem
    ? clearanceList.push('1')
    : clearanceList.push('0');
  const sthSpecialOrder = skuDetailsData?.skuPricingAndAvailability?.specialOrder ?? false;
  const vdp =
    skuDetailsData?.skuPricingAndAvailability?.skuAvailabilityInfo?.availableInVdpStore ?? false;
  const productCorePrice = skuDetailsData?.skuPricingAndAvailability?.corePrice.toString() ?? '';

  if (!bopusList[0] && !sthList[0]) {
    productNotAvailable = true;
  } else {
    productNotAvailable = false;
  }

  const [bopus, sth, ndd] = determineOptionsToDisplayButtons(skuDetailsData?.fulfillmentOptions);
  const productFulfillmentOptionSeenByCustomer = [
    [bopus, sth, ndd]
      .filter((option) => option?.available)
      .map((option) => String(option?.fulfillmentTypeIdOriginal))
      .join(':'),
  ];

  const url = root.location?.href ?? '';
  const documentReferrer = root.document?.referrer ?? '';
  let referrer = '';

  if (documentReferrer) {
    referrer = documentReferrer;
  } else {
    referrer = url;
  }

  let productFindingMethod = '';
  if (url.includes('intcmp')) {
    productFindingMethod = 'Internal campaign';
  } else if (url.includes('cmpid') || (!!referrer && !referrer.includes('autozone.com'))) {
    productFindingMethod = 'External Channel';
  }

  const partialData = {
    partTerminologyId: productRepositoryIdFromShelfData
      ? productRepositoryIdFromShelfData
      : ecommProductId,
    partGroupId: productDetails.partGroupId,
  };

  return {
    autoCorrectedKeyword,
    nddaftercutoff,
    productPartTypeIdList,
    sthSpecialOrderList,
    dealBundleCountList,
    productPart: [partTypeName ?? ''],
    productId: [itemId ?? ''],
    productPartNumberList: [partNumber ?? ''],
    productLineCode: [lineCode ?? ''],
    productBrand: [(subBrandName || brandName) ?? ''],
    sthSpecialOrder: [sthSpecialOrder],
    productFindingMethod,
    productPrice: [productPrice],
    dealFlag: [dealFlag],
    productCorePrice: [productCorePrice],
    productNotAvailable: [productNotAvailable],
    vdp: [vdp],
    fulfillmentAvailabilityList,
    bopusList,
    sthList,
    nddEligibleList,
    ...Object.fromEntries(Object.entries(partialData).map(([key, value]) => [key, [value]])),
    ...(!!searchRedirectText && {
      searchResultCount: '1',
    }),
    productFitmentCheckMessage,
    sisterStoreAvailableList,
    clearanceList,
    productDealMessage,
    productFulfillmentOptionSeenByCustomer,
  };
};

export default productDetailAnalytics;
